import { api } from '@/api/client'
import type { ArchiveTimelineSegment, TrassirCamera, TrassirServer } from './types'

export async function fetchTrassirServers(): Promise<TrassirServer[]> {
  return api<TrassirServer[]>('/api/trassir/servers')
}

export async function fetchTrassirServersByStore(storeId: number): Promise<TrassirServer[]> {
  return api<TrassirServer[]>(`/api/trassir/servers/store/${storeId}`)
}

function normalizeChannel(raw: unknown): TrassirCamera | null {
  if (!raw || typeof raw !== 'object') return null
  const row = raw as Record<string, unknown>
  const guid = String(row.guid || row.channel || row.id || '').trim()
  if (!guid) return null
  const name = String(row.name || row.title || guid).trim() || guid
  return { guid, name }
}

export async function fetchChannels(serverId: number): Promise<TrassirCamera[]> {
  const data = await api<unknown>(`/api/trassir/servers/${serverId}/channels`)
  const list = Array.isArray(data)
    ? data
    : Array.isArray((data as { channels?: unknown[] })?.channels)
      ? (data as { channels: unknown[] }).channels
      : []
  const out: TrassirCamera[] = []
  const seen = new Set<string>()
  for (const item of list) {
    const cam = normalizeChannel(item)
    if (!cam || seen.has(cam.guid)) continue
    seen.add(cam.guid)
    out.push(cam)
  }
  return out
}

export async function fetchArchiveCalendar(
  serverId: number,
  guid: string,
): Promise<{ calendar?: string[] }> {
  return api(`/api/trassir/servers/${serverId}/archive/${guid}/calendar`)
}

export async function fetchArchiveTimeline(
  serverId: number,
  guid: string,
  date: string,
): Promise<{ timeline?: ArchiveTimelineSegment[] }> {
  return api(`/api/trassir/servers/${serverId}/archive/${guid}/timeline?date=${encodeURIComponent(date)}`)
}

export async function fetchMediaContext(
  serverId: number,
  guid?: string,
): Promise<{ videoPort: number; utcOffsetMinutes: number; host: string }> {
  const q = guid ? `?guid=${encodeURIComponent(guid)}` : ''
  return api(`/api/trassir/servers/${serverId}/media-context${q}`)
}

export async function downloadArchiveExport(
  serverId: number,
  guid: string,
  opts: {
    start_ts?: number
    end_ts?: number
    date?: string
    start_time?: string
    end_time?: string
  },
  onProgress?: (pct: number, label: string) => void,
): Promise<{ blob: Blob; filename: string }> {
  const res = await fetch(`/api/trassir/servers/${serverId}/archive/${guid}/export`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(opts),
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new Error((err as { error?: string }).error || res.statusText)
  }

  let filename = `archive_${guid}.mp4`
  const disp = res.headers.get('Content-Disposition')
  if (disp) {
    const m = disp.match(/filename="?([^"]+)"?/)
    if (m) filename = m[1]
  }

  const reader = res.body?.getReader()
  if (!reader) {
    const blob = await res.blob()
    return { blob, filename }
  }

  const contentLength = Number(res.headers.get('Content-Length') || 0)
  let received = 0
  const chunks: Uint8Array[] = []
  while (true) {
    const { done, value } = await reader.read()
    if (done) break
    chunks.push(value)
    received += value.length
    if (contentLength > 0) {
      onProgress?.(Math.round((received / contentLength) * 100), `${Math.round((received / contentLength) * 100)}%`)
    } else {
      onProgress?.(0, `${Math.round(received / 1024)} КБ`)
    }
  }
  const blob = new Blob(chunks, { type: 'video/mp4' })
  return { blob, filename }
}

export async function fetchScreenshot(
  serverId: number,
  guid: string,
  timestamp?: string | number,
): Promise<Blob> {
  const q =
    timestamp != null && timestamp !== ''
      ? `?timestamp=${encodeURIComponent(String(timestamp))}`
      : ''
  const res = await fetch(`/api/trassir/servers/${serverId}/screenshot/${guid}${q}`, {
    credentials: 'include',
  })
  if (!res.ok) throw new Error('Не удалось получить скриншот')
  return res.blob()
}

export async function checkServerHealth(serverId: number): Promise<unknown> {
  return api(`/api/trassir/servers/${serverId}/health`)
}

export interface TrassirPosEvent {
  id: number
  server_id: number
  terminal_id: number | null
  op_id: string
  type: string
  type_label: string
  pos_terminal: string | null
  pos_terminal_name: string | null
  position: string | null
  price: number | null
  quantity: number | null
  weight: number | null
  text: string | null
  event_ts_us: number
  receipt_ts_us: number | null
  event_at: string | null
  is_alert: boolean
  channel_guid?: string | null
}

export async function fetchTerminalPosEvents(
  terminalId: number,
  opts?: { limit?: number; alerts?: boolean; since_us?: number },
): Promise<TrassirPosEvent[]> {
  const q = new URLSearchParams()
  if (opts?.limit) q.set('limit', String(opts.limit))
  if (opts?.alerts) q.set('alerts', '1')
  if (opts?.since_us) q.set('since_us', String(opts.since_us))
  const qs = q.toString()
  const data = await api<{ events?: TrassirPosEvent[] }>(
    `/api/trassir/terminals/${terminalId}/pos-events${qs ? `?${qs}` : ''}`,
  )
  return data.events || []
}

export async function fetchServerPosTerminals(
  serverId: number,
): Promise<{ guid: string; name: string | null; events_count: number }[]> {
  const data = await api<{ terminals?: { guid: string; name: string | null; events_count: number }[] }>(
    `/api/trassir/servers/${serverId}/pos-terminals`,
  )
  return data.terminals || []
}
